import { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { CELL_HEIGHT } from '../../components/Spreadsheet/constants';

// Hook to handle printing multiple components from external refs
export const useMultiRefPrint = (
    firstRef: React.MutableRefObject<HTMLDivElement | null>,
    secondRef: React.MutableRefObject<HTMLDivElement | null>,
    onHandlePrintExtra?: () => void,
    header: React.ReactNode = null
) => {
    // Container ref that will hold clones of both component contents for printing
    const combinedPrintRef = useRef<HTMLDivElement | null>(null);
    const [renderForPrint, setRenderForPrint] = useState<boolean>(false);

    // State to store cloned content from refs
    const [firstContent, setFirstContent] = useState<string>('');
    const [secondContent, setSecondContent] = useState<string>('');

    // Function to clone content from refs
    const cloneRefContents = () => {
        if (firstRef.current) {
            setFirstContent(firstRef.current.innerHTML);
        }
        if (secondRef.current) {
            setSecondContent(secondRef.current.innerHTML);
        }
    };

    const handlePrint = useReactToPrint({
        content: () => {
            onHandlePrintExtra && onHandlePrintExtra();
            return combinedPrintRef.current;
        },
        onAfterPrint: () => {
            setRenderForPrint(false);
        },
    });

    useEffect(() => {
        if (renderForPrint) {
            // Clone ref contents before printing
            cloneRefContents();

            // Small delay to ensure content is ready for printing
            setTimeout(() => {
                handlePrint();
            }, 100);
        }
    }, [renderForPrint, handlePrint]);

    // Hidden print container
    const hiddenPrintContainer = renderForPrint && (
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
            <div ref={combinedPrintRef}>
                {header}
                {/* Use actual content from the refs */}
                <div
                    className="print-component"
                    dangerouslySetInnerHTML={{ __html: firstContent }}
                    style={{ marginLeft: CELL_HEIGHT }}
                />
                <div
                    className="print-component"
                    dangerouslySetInnerHTML={{ __html: secondContent }}
                />
            </div>
        </div>
    );

    return {
        renderForPrint,
        setRenderForPrint,
        hiddenPrintContainer,
    };
};
